import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Store, Package, Users, ChevronLeft, ArrowRight, CalendarDays, Megaphone, BookOpen, Video, MessageSquare } from 'lucide-react';
import { fetchMarketplace, type MarketplaceCreator } from '@/services/creatorApi';

type CategoryFilter = '' | 'classes' | 'webinars' | 'merch' | 'community' | 'single' | 'series';

const MarketplacePage: React.FC = () => {
  const [creators, setCreators] = useState<MarketplaceCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('');
  const [topic, setTopic] = useState('');
  const [topics, setTopics] = useState<{ slug: string; label: string }[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Curated subject topics — one fetch, chips hide until it lands.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/marketplace/taxonomy').then(r => r.json())
      .then(d => { if (!cancelled && Array.isArray(d?.categories)) setTopics(d.categories); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when category/topic changes
  useEffect(() => {
    setPage(1);
  }, [category, topic]);

  const loadCreators = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMarketplace({
        search: debouncedSearch || undefined,
        category: category || undefined,
        topic: topic || undefined,
        page,
        limit: 20,
        // Dev-only preview: ?preview=ita-preview surfaces staged ITA classes so we can
        // see the populated marketplace before they publish. No-op on prod (not shipped).
        preview: new URLSearchParams(window.location.search).get('preview') || undefined,
      });
      setCreators(data.creators);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load marketplace:', error);
      setCreators([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, category, topic, page]);

  useEffect(() => {
    loadCreators();
  }, [loadCreators]);

  // Preserve ?preview=… when linking into a store so staged ITA classes stay visible.
  const previewParam = new URLSearchParams(window.location.search).get('preview');
  const storeHref = (slug: string) => `/creators/${slug}${previewParam ? `?preview=${encodeURIComponent(previewParam)}` : ''}`;

  const categoryFilters: { label: string; value: CategoryFilter }[] = [
    { label: 'All', value: '' },
    { label: 'Classes', value: 'classes' },
    { label: 'Featured events', value: 'webinars' },
    { label: 'Merch', value: 'merch' },
    { label: 'Community', value: 'community' },
    { label: 'Single Videos', value: 'single' },
    { label: 'Series / Courses', value: 'series' },
  ];

  return (
    <main
      style={{
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
    <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 24px 96px' }}>
      {/* Compact header: the page used to spend ~700px on breadcrumb, eyebrow, a 56px
          title, a two-line subhead, a divider, a stacked search row, two chip rows and
          the casting-calls card before the first tile (Joe, Sep 9). Now: one line of
          title + subhead, then filters, then content. */}
      {/* Cream card behind the header and filters on the dark page (Joe, Sep 9): the
          old dark top with faded white text read as heavy and low-contrast. */}
      <section style={{ margin: '20px 0 22px', padding: '22px 24px 18px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
      <header className="flex flex-wrap items-end justify-between gap-4" style={{ padding: '0 0 16px' }}>
        <div>
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 'clamp(28px, 3vw, 38px)', lineHeight: 1.05, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em' }}
          >
            Learn from the room.
          </h1>
          <p className="m-0" style={{ marginTop: 6, color: 'var(--hj-beige-ink)', fontSize: 14.5, lineHeight: 1.45, maxWidth: 560 }}>
            Live classes, featured events and on-demand courses from working actors, casting directors and coaches.
          </p>
        </div>
        {/* Full width on phones with the search box taking whatever the Calendar pill
            leaves; a fixed 260px search plus the pill overflowed a 375px screen. */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 min-w-0 sm:flex-none sm:w-[260px]">
            <Search
              className="absolute w-4 h-4"
              style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--hj-beige-ink-3)' }}
            />
            <Input
              placeholder="Search creators or products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              style={{ background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', borderRadius: 999 }}
            />
          </div>
          <Link
            to="/calendar"
            className="inline-flex items-center gap-1.5 no-underline"
            style={{ padding: '9px 14px', borderRadius: 999, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            <CalendarDays className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-2)' }} />
            Calendar
          </Link>
        </div>
      </header>

      {/* One row: format chips, then a topic picker at the end of the row. */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {categoryFilters.map((f) => {
            const active = category === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setCategory(f.value)}
                className="inline-flex items-center transition-colors"
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: active ? 'oklch(0.18 0.005 70)' : '#fff',
                  border: `1px solid ${active ? 'oklch(0.18 0.005 70)' : 'var(--hj-beige-line)'}`,
                  color: active ? '#fff' : 'var(--hj-beige-ink)',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {f.label}
              </button>
            );
          })}
          {/* Subject topic as a picker instead of a second row of nine chips. */}
          {topics.length > 0 && (
            <label className="inline-flex items-center" style={{ marginLeft: 'auto' }}>
              <span className="sr-only">Topic</span>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                style={{
                  padding: '7px 32px 7px 14px', borderRadius: 999, fontSize: 13, fontWeight: topic ? 700 : 500, fontFamily: 'inherit', cursor: 'pointer',
                  background: topic ? 'var(--hj-yellow)' : '#fff', color: 'var(--hj-beige-ink)',
                  border: `1px solid ${topic ? 'var(--hj-yellow)' : 'var(--hj-beige-line)'}`, appearance: 'none', WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%23999' stroke-width='1.5'/></svg>")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
                }}
              >
                <option value="">All topics</option>
                {topics.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>
      </section>



      {!loading && (
        <div className="mono-label" style={{ marginBottom: 16, color: 'var(--hj-ink)' }}>
          {total} creator{total !== 1 ? 's' : ''} found
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center" style={{ padding: '80px 0' }}>
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
          <p className="m-0" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-ink)' }}>
            Loading marketplace…
          </p>
        </div>
      )}

      {!loading && creators.length === 0 && (
        <div
          className="text-center"
          style={{
            padding: '60px 24px',
            borderRadius: 'var(--hj-r-md)',
            border: '1.5px dashed var(--hj-beige-line)',
            background: 'var(--hj-beige)',
            color: 'var(--hj-beige-ink)',
          }}
        >
          <Store className="w-10 h-10 mx-auto" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 12 }} />
          {debouncedSearch || category || topic ? (
            <>
              <h3
                className="font-hj-display m-0"
                style={{ fontSize: 20, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em' }}
              >
                No results.
              </h3>
              <p className="m-0" style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>
                Try adjusting your search or filters.
              </p>
            </>
          ) : (
            <>
              <h3
                className="font-hj-display m-0"
                style={{ fontSize: 20, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em' }}
              >
                Courses & classes coming soon!
              </h3>
              <p className="m-0" style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hj-beige-ink-2)', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
                Stay tuned — and if you have a course to sell, set up your shop and start earning.
              </p>
              <Link
                to="/my-store"
                className="inline-flex items-center no-underline"
                style={{
                  marginTop: 16,
                  padding: '10px 20px',
                  borderRadius: 999,
                  background: 'var(--hj-yellow)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 13.5,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                }}
              >
                Set up your shop — free
              </Link>
            </>
          )}
        </div>
      )}

      {!loading && creators.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {creators.map((creator) => {
            const nextClass = creator.nextEventDate ? new Date(creator.nextEventDate) : null;
            const nextClassLabel =
              nextClass && !isNaN(nextClass.getTime())
                ? nextClass.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                : null;
            // Dated events: classes (ITA-style) vs featured events get their own label/noun.
            // A store with both is labeled by its classes.
            const isEventStore = !!(creator.hasClasses || creator.hasWebinars);
            const eventNoun = creator.hasClasses ? 'class' : 'featured event';
            // A store can carry live events AND on-demand courses. Letting events win
            // outright hid the courses completely — MYST reads "Webinars" while also
            // selling a video course — so name both. The live format leads because
            // it's the dated thing a buyer has to act on.
            const liveTag = creator.hasClasses ? 'Classes' : 'Featured events';
            const eventTag = creator.hasSeries ? `${liveTag} & courses` : liveTag;
            // Courses-only stores get their own badge — event badges win when both apply.
            const isCourseStore = !isEventStore && !!creator.hasSeries;
            // INVARIANT: every store tile shows a format badge. Dated events win,
            // then courses, then anything else falls back to 'Videos' — so a new
            // productType can never render an untagged tile (bit us twice).
            const fallbackTag = !isEventStore && !isCourseStore ? 'Videos' : null;
            // Empty bios still get an inviting line; a real bio always wins.
            const about =
              creator.bio?.trim() ||
              (creator.hasClasses
                ? 'Live acting classes & workshops with working industry pros.'
                : creator.hasWebinars
                ? 'Live featured events & Q&As with the Hey Joe community.'
                : creator.hasSeries
                ? 'On-demand courses from working industry pros.'
                : '');
            const initials =
              creator.displayName?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
            return (
              <Link
                key={creator.slug}
                to={storeHref(creator.slug)}
                className="group flex flex-col overflow-hidden"
                style={{
                  borderRadius: 'var(--hj-r-md)',
                  background: 'var(--hj-beige)',
                  border: '1px solid var(--hj-beige-line)',
                  textDecoration: 'none',
                  color: 'var(--hj-beige-ink)',
                }}
              >
                {/* Featured banner — the creator's uploaded banner, or a warm gradient fallback */}
                <div
                  className="relative overflow-hidden"
                  style={{
                    // Same 16:5 header for every card so heights line up — a full
                    // banner when set, the brand gradient otherwise.
                    aspectRatio: '16 / 5',
                    background:
                      'radial-gradient(120% 140% at 85% 0%, oklch(0.34 0.045 72) 0%, transparent 60%), linear-gradient(135deg, oklch(0.19 0.01 65), oklch(0.28 0.03 70))',
                  }}
                >
                  {creator.bannerImage && (
                    <img
                      src={creator.bannerImage.startsWith('http') || creator.bannerImage.startsWith('/api/') ? creator.bannerImage : `/api/s3/${creator.bannerImage}`}
                      alt=""
                      className="absolute inset-0 w-full h-full"
                      style={{ objectFit: 'cover' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span
                    className="mono-label absolute inline-flex items-center gap-1"
                    style={{
                      top: 12,
                      left: 14,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'var(--hj-yellow)',
                      color: 'oklch(0.18 0.005 70)',
                      fontWeight: 700,
                    }}
                  >
                    {isEventStore
                      ? <><CalendarDays className="w-3 h-3" /> {eventTag}</>
                      : isCourseStore
                      ? <><BookOpen className="w-3 h-3" /> Courses</>
                      : <><Video className="w-3 h-3" /> {fallbackTag}</>}
                  </span>
                </div>

                {/* Body */}
                <div className="flex flex-col flex-1" style={{ padding: '0 18px 18px' }}>
                  {/* Avatar — white ITA logo needs a dark disc to read */}
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{
                      width: 64,
                      height: 64,
                      // Every card has a 16:5 header; the avatar pokes up into it a
                      // little (its beige ring reads over the banner/gradient). Same
                      // value on every card so the avatars line up across the row.
                      // position+zIndex so it paints OVER the relative-positioned
                      // header (otherwise the banner covers it — the earlier clip bug).
                      marginTop: -18,
                      position: 'relative',
                      zIndex: 1,
                      background: 'oklch(0.18 0.005 70)',
                      border: '3px solid var(--hj-beige)',
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 700,
                      fontFamily: 'var(--hj-font-mono)',
                    }}
                  >
                    {creator.imageUrl ? (
                      <img
                        src={creator.imageUrl.startsWith('http') || creator.imageUrl.startsWith('/api/') ? creator.imageUrl : `/api/s3/${creator.imageUrl}`}
                        alt={creator.displayName}
                        className="w-full h-full"
                        style={/(?:logo|wordmark)/i.test(creator.imageUrl) ? { objectFit: 'contain', padding: 9 } : { objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      initials
                    )}
                  </div>

                  <h3
                    className="font-hj-display m-0"
                    style={{ marginTop: 10, fontSize: 18, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em', fontWeight: 700, lineHeight: 1.15 }}
                  >
                    {creator.displayName}
                  </h3>

                  <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 8 }}>
                    <span
                      className="inline-flex items-center gap-1 mono-label"
                      style={{ padding: '3px 8px', borderRadius: 999, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)' }}
                    >
                      <Package className="w-3 h-3" />
                      {creator.productCount} {isEventStore ? (creator.productCount === 1 ? eventNoun : `${eventNoun}s`) : (creator.productCount === 1 ? 'item' : 'items')}
                    </span>
                    {(creator.totalMembers ?? 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 mono-label"
                        style={{ padding: '3px 8px', borderRadius: 999, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)' }}
                      >
                        <Users className="w-3 h-3" />
                        {creator.totalMembers}
                      </span>
                    )}
                    {creator.freeWithPlus && (
                      <span
                        className="inline-flex items-center gap-1 mono-label"
                        style={{ padding: '3px 8px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'var(--hj-beige-ink)', fontWeight: 700 }}
                      >
                        Free with Plus
                      </span>
                    )}
                  </div>

                  <p
                    className="line-clamp-2 m-0"
                    style={{ marginTop: 10, fontSize: 13, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5, minHeight: 39 }}
                  >
                    {about || ''}
                  </p>

                  <div
                    className="flex items-center justify-between mt-auto"
                    style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hj-beige-line)' }}
                  >
                    {/* A store is not one thing to buy, so no price here: what's next is the reason to click.
                        A replay whose host is still answering questions beats a far-off next date. */}
                    {creator.activeReplay ? (
                      <span
                        className="inline-flex items-center gap-1.5"
                        style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}
                      >
                        <MessageSquare className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-2)' }} />
                        Active replay until {new Date(creator.activeReplay.until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    ) : nextClassLabel ? (
                      <span
                        className="inline-flex items-center gap-1.5"
                        style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink-2)' }}
                      >
                        <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-3)' }} />
                        Next {eventNoun} {nextClassLabel}
                      </span>
                    ) : <span />}
                    <span
                      className="inline-flex items-center gap-1 mono-label group-hover:gap-1.5 transition-all"
                      style={{ color: 'var(--hj-beige-ink)', fontWeight: 700 }}
                    >
                      View store
                      <ArrowRight size={11} strokeWidth={2.4} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3" style={{ marginTop: 32 }}>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="disabled:cursor-not-allowed"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-beige-line)',
              color: 'var(--hj-beige-ink)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              opacity: page <= 1 ? 0.4 : 1,
              minHeight: 40,
            }}
          >
            Previous
          </button>
          <span className="mono-label tabular" style={{ color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="disabled:cursor-not-allowed"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              opacity: page >= totalPages ? 0.4 : 1,
              minHeight: 40,
            }}
          >
            Next
          </button>
        </div>
      )}
      {/* Casting calls — live 2026-08-17, but they live on CASTING, not here: the
          board is a tab on the talent profile at casting.heyjoe.io/casting-calls.
          This was a relative <Link to="/casting-calls">, and there is no such route
          in this app — the SPA serves index.html for every path, so it returned 200
          and then rendered the 404 page. HomePage has always linked out correctly;
          only this card did not. Never point it at castingstation — not our product,
          not where these jobs are. */}
      <a
        href="https://casting.heyjoe.io/casting-calls"
        className="group flex items-center gap-4 no-underline"
        style={{
          marginTop: 36,
          padding: '16px 20px',
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-hairline)',
          color: 'var(--hj-ink)',
        }}
      >
        <div
          className="rounded-full flex items-center justify-center flex-shrink-0"
          style={{ width: 44, height: 44, background: 'oklch(0.18 0.005 70)', color: '#fff' }}
        >
          <Megaphone className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 3 }}>Casting calls</div>
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.45 }}>
            Creator projects are casting from the Hey Joe community — browse open calls and apply free.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 mono-label flex-shrink-0 group-hover:gap-1.5 transition-all"
          style={{ color: 'var(--hj-ink)', fontWeight: 700 }}
        >
          Browse
          <ArrowRight size={12} strokeWidth={2.4} />
        </span>
      </a>
    </div>
    </main>
  );
};

export default MarketplacePage;
